import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";

const SERVICES = [
  "Prima visita e consulto",
  "Implantologia avanzata",
  "All-on-4 / All-on-6",
  "Protesi e faccette",
  "Ortodonzia invisibile (Invisalign)",
  "Parodontologia e igiene",
  "Odontoiatria pediatrica",
  "Emergenza / dolore",
];

const TIMES = ["Mattina", "Pomeriggio", "Indifferente"];

const inputCls =
  "w-full rounded-xl border border-line bg-paper px-4 py-3 text-sm text-ink placeholder:text-mutedc/60 outline-none focus:border-teal focus:ring-2 focus:ring-teal/15 transition-all";
const labelCls = "block text-xs font-semibold uppercase tracking-[0.14em] text-mutedc mb-2";

export default function AppointmentForm() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: SERVICES[0],
    preferred_time: TIMES[0],
    message: "",
    consent: false,
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: k === "consent" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Acconsenti al trattamento dei dati per inviare la richiesta.");
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/appuntamenti`,
        form
      );
      setDone(data.ref);
      toast.success(`Richiesta inviata! Ti richiameremo al più presto.`);
      setForm({ name: "", phone: "", email: "", service: SERVICES[0], preferred_time: TIMES[0], message: "", consent: false });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Invio non riuscito. Chiamaci allo 011 19667070.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-3xl border border-teal/20 bg-teal-mist/60 p-10 text-center" data-testid="form-success-card">
        <CheckCircle2 size={44} className="mx-auto text-teal mb-4" />
        <p className="font-display text-3xl text-ink mb-2">Richiesta ricevuta</p>
        <p className="text-sm text-mutedc leading-relaxed">
          Il tuo codice di riferimento è <strong className="text-teal">{done}</strong>.
          Ti richiameremo al più presto per confermare l'appuntamento.
        </p>
        <button
          onClick={() => setDone(null)}
          data-testid="form-reset-button"
          className="mt-6 text-sm font-semibold text-teal hover:text-teal-deep underline underline-offset-4"
        >
          Invia un'altra richiesta
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-3xl border border-line bg-paper p-6 md:p-8 shadow-soft" data-testid="appointment-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="af-name">Nome e cognome *</label>
          <input
            id="af-name"
            required
            value={form.name}
            onChange={set("name")}
            data-testid="form-name-input"
            className={inputCls}
            placeholder="Maria Rossi"
          />
        </div>
        <div>
          <label className={labelCls} htmlFor="af-phone">Telefono *</label>
          <input
            id="af-phone"
            required
            type="tel"
            value={form.phone}
            onChange={set("phone")}
            data-testid="form-phone-input"
            className={inputCls}
            placeholder="333 0000000"
          />
        </div>
        <div>
          <label className={labelCls} htmlFor="af-email">Email (facoltativa)</label>
          <input
            id="af-email"
            type="email"
            value={form.email}
            onChange={set("email")}
            data-testid="form-email-input"
            className={inputCls}
            placeholder="maria@esempio.it"
          />
        </div>
        <div>
          <label className={labelCls} htmlFor="af-service">Servizio *</label>
          <select
            id="af-service"
            value={form.service}
            onChange={set("service")}
            data-testid="form-service-select"
            className={inputCls}
          >
            {SERVICES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="af-time">Preferenza oraria</label>
          <select
            id="af-time"
            value={form.preferred_time}
            onChange={set("preferred_time")}
            data-testid="form-time-select"
            className={inputCls}
          >
            {TIMES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="af-message">Messaggio</label>
          <textarea
            id="af-message"
            rows={4}
            value={form.message}
            onChange={set("message")}
            data-testid="form-message-textarea"
            className={`${inputCls} resize-none`}
            placeholder="Raccontaci brevemente di cosa hai bisogno…"
          />
        </div>
        <div className="sm:col-span-2 flex items-start gap-3">
          <input
            id="af-consent"
            type="checkbox"
            checked={form.consent}
            onChange={set("consent")}
            data-testid="form-consent-checkbox"
            className="mt-1 w-4 h-4 accent-teal"
          />
          <label htmlFor="af-consent" className="text-xs leading-relaxed text-mutedc">
            Acconsento al trattamento dei dati personali per essere ricontattato in merito alla richiesta
            di appuntamento.
          </label>
        </div>
      </div>

      <button
        type="submit"
        disabled={sending}
        data-testid="form-submit-button"
        className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full bg-teal text-white font-semibold text-sm px-6 py-4 hover:bg-teal-deep disabled:opacity-60 transition-colors duration-300"
      >
        {sending && <Loader2 size={16} className="animate-spin" />}
        {sending ? "Invio in corso…" : "Richiedi l'appuntamento"}
      </button>
    </form>
  );
}